/**
 * CAOS-659 — a panel opened against the control that opened it stays on screen.
 *
 * The app launcher, the navigation gadget, the tab row's overflow menu, the overflow menu, the object
 * caret and the tooltip each placed a panel from its trigger's rectangle and stopped there. Each one
 * answered "does it fit?" differently, or not at all, and the ones that did ask measured against
 * `window.innerWidth`, which counts the vertical scrollbar. At a 390px window with a scrollbar, the
 * part of the page anyone can see is about 375px wide, so a menu clamped to 390px still ran 7 to 9px
 * off the right edge. The tooltip asked nothing and was centred on its target, so a target near the
 * left edge pushed its tip off the screen.
 *
 * This is the one answer. {@link placePanel} is pure: rectangles in, a position out.
 * {@link anchorPanel} measures the DOM and writes that position onto a `position: fixed` panel.
 * The rules, in order:
 *
 *   1. Open on the preferred side of the anchor.
 *   2. If the panel does not fit there and the opposite side has room, or more room, open there.
 *   3. Slide along the edge until the whole panel is inside the viewport, less a margin. A panel
 *      that fits on neither side is still kept on screen, even if it has to cover its anchor.
 *   4. Report where the anchor's centre falls along the panel's edge, so a pointer drawn there
 *      still aims at the trigger after the slide.
 *
 * The viewport is the VISIBLE one ({@link visibleViewport}): the root element's client size, which
 * excludes a scrollbar. A panel larger than the viewport starts at the margin; nothing can put all of
 * it on screen.
 */

export type PanelSide = 'top' | 'bottom' | 'left' | 'right';
export type PanelAlign = 'start' | 'center' | 'end';

/** The anchor's box in viewport coordinates, the shape `getBoundingClientRect()` returns. */
export interface AnchorRect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

export interface PanelSize {
  readonly width: number;
  readonly height: number;
}

export interface ViewportSize {
  readonly width: number;
  readonly height: number;
}

export interface PanelPlacementOptions {
  /** The side of the anchor the panel prefers to open on. */
  readonly side: PanelSide;
  /** How the panel lines up with the anchor along that side. Defaults to `center`. */
  readonly align?: PanelAlign;
  /** Space between the anchor and the panel. Defaults to 0. */
  readonly gap?: number;
  /** The least distance kept between the panel and any viewport edge. Defaults to 8. */
  readonly margin?: number;
}

export interface PanelPlacementRequest extends PanelPlacementOptions {
  readonly anchor: AnchorRect;
  readonly panel: PanelSize;
  readonly viewport: ViewportSize;
}

export interface PanelPlacement {
  /** Viewport coordinates of the panel's top-left corner. */
  readonly left: number;
  readonly top: number;
  /** The side the panel actually opened on. */
  readonly side: PanelSide;
  /** True when it opened on the opposite side from the one asked for. */
  readonly flipped: boolean;
  /** True when it was slid to stay inside the viewport. */
  readonly shifted: boolean;
  /**
   * Where the anchor's centre falls along the panel's edge that faces it, measured from the panel's
   * left (for `top`/`bottom`) or top (for `left`/`right`), and kept within the panel. A pointer drawn
   * at this offset aims at the trigger.
   */
  readonly pointer: number;
}

const OPPOSITE: Readonly<Record<PanelSide, PanelSide>> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

/** Keep `start` so that `[start, start + size]` sits inside `[margin, limit - margin]` where it can. */
function slide(start: number, size: number, limit: number, margin: number): number {
  return Math.max(margin, Math.min(start, limit - size - margin));
}

/** Place a panel against an anchor so that it stays on screen. Pure. */
export function placePanel(request: PanelPlacementRequest): PanelPlacement {
  const { anchor, panel, viewport } = request;
  const align = request.align ?? 'center';
  const gap = request.gap ?? 0;
  const margin = request.margin ?? 8;

  const roomOn = (side: PanelSide): number => {
    switch (side) {
      case 'top':
        return anchor.top - gap - margin;
      case 'bottom':
        return viewport.height - anchor.bottom - gap - margin;
      case 'left':
        return anchor.left - gap - margin;
      case 'right':
        return viewport.width - anchor.right - gap - margin;
    }
  };

  const vertical = request.side === 'top' || request.side === 'bottom';
  const depth = vertical ? panel.height : panel.width;

  let side = request.side;
  if (roomOn(side) < depth) {
    const other = OPPOSITE[side];
    if (roomOn(other) >= depth || roomOn(other) > roomOn(side)) side = other;
  }

  // Along the axis that leaves the anchor: flush against the chosen side.
  const main =
    side === 'top'
      ? anchor.top - gap - panel.height
      : side === 'bottom'
        ? anchor.bottom + gap
        : side === 'left'
          ? anchor.left - gap - panel.width
          : anchor.right + gap;

  // Along the anchor's edge: lined up as asked.
  const anchorStart = vertical ? anchor.left : anchor.top;
  const anchorEnd = vertical ? anchor.right : anchor.bottom;
  const breadth = vertical ? panel.width : panel.height;
  const cross =
    align === 'start' ? anchorStart : align === 'end' ? anchorEnd - breadth : (anchorStart + anchorEnd) / 2 - breadth / 2;

  const mainPlaced = slide(main, depth, vertical ? viewport.height : viewport.width, margin);
  const crossPlaced = slide(cross, breadth, vertical ? viewport.width : viewport.height, margin);
  const pointer = Math.max(0, Math.min(breadth, (anchorStart + anchorEnd) / 2 - crossPlaced));

  return {
    left: vertical ? crossPlaced : mainPlaced,
    top: vertical ? mainPlaced : crossPlaced,
    side,
    flipped: side !== request.side,
    shifted: mainPlaced !== main || crossPlaced !== cross,
    pointer,
  };
}

/**
 * The part of the window a person can see: the root element's client size, which excludes a
 * scrollbar, where `innerWidth` includes it. A root with no size means no layout has run (a test
 * DOM), and the window's own size is then all there is to go on.
 */
export function visibleViewport(win: Window = window): ViewportSize {
  const root = win.document.documentElement;
  return { width: root.clientWidth || win.innerWidth, height: root.clientHeight || win.innerHeight };
}

/**
 * Measure a `position: fixed` panel and its anchor, place the panel with {@link placePanel}, and write
 * the result onto it: `left`/`top`, `data-side` for the side it opened on, and
 * `--caos-panel-pointer` for where a pointer would aim. The panel must be displayed when this runs,
 * or it measures nothing.
 */
export function anchorPanel(
  panel: HTMLElement,
  anchor: Element | AnchorRect,
  options: PanelPlacementOptions,
): PanelPlacement {
  const anchorRect = anchor instanceof Element ? anchor.getBoundingClientRect() : anchor;
  const box = panel.getBoundingClientRect();
  const size: PanelSize = { width: box.width || panel.offsetWidth, height: box.height || panel.offsetHeight };
  const win = panel.ownerDocument.defaultView ?? window;
  const placed = placePanel({ anchor: anchorRect, panel: size, viewport: visibleViewport(win), ...options });
  panel.style.left = `${Math.round(placed.left)}px`;
  panel.style.top = `${Math.round(placed.top)}px`;
  panel.dataset['side'] = placed.side;
  panel.style.setProperty('--caos-panel-pointer', `${Math.round(placed.pointer)}px`);
  return placed;
}
